class CompleteSystemOrbitSpeed extends HTMLElement {
  static RING_SELECTOR = '.complete-system__hero-ring-svg--outer, .complete-system__hero-ring-svg--middle, .complete-system__hero-ring-svg--inner';

  static PILL_SELECTOR = '.complete-system-benefit-pill';

  static PILL_FLOAT_ANIMATION = 'complete-system-pill-float';

  static TRANSITION_MS = 1000;

  #controller = null;
  #heroCenter = null;
  #animations = [];
  #rafId = null;
  #currentSpeed = 1;
  #baseSpeed = 1;
  #targetSpeed = 1;
  #transitionStart = 0;
  #transitionFromSpeed = 1;

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      return;
    }

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#heroCenter = this.closest('.complete-system__hero-center');

    if (!this.#heroCenter) {
      return;
    }

    requestAnimationFrame(() => {
      this.#collectAnimations();
    });

    this.#heroCenter.addEventListener('mouseover', this.#onMouseOver, { signal });
    this.#heroCenter.addEventListener('mouseout', this.#onMouseOut, { signal });
  }

  disconnectedCallback() {
    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
    }

    for (const animation of this.#animations) {
      animation.playbackRate = this.#baseSpeed;
    }

    for (const pill of this.querySelectorAll(CompleteSystemOrbitSpeed.PILL_SELECTOR)) {
      this.#resumePillFloat(pill);
    }

    this.#controller?.abort();
    this.#controller = null;
    this.#heroCenter = null;
    this.#animations = [];
    this.#rafId = null;
  }

  #collectAnimations() {
    const rings = this.#heroCenter.querySelectorAll(CompleteSystemOrbitSpeed.RING_SELECTOR);
    const animations = [];

    for (const ring of rings) {
      for (const animation of ring.getAnimations()) {
        animations.push(animation);
      }
    }

    this.#animations = animations;

    if (animations.length === 0) {
      return;
    }

    this.#baseSpeed = animations[0].playbackRate || 1;
    this.#currentSpeed = this.#baseSpeed;
    this.#targetSpeed = this.#baseSpeed;
  }

  #setTargetSpeed(target) {
    if (target === this.#targetSpeed && this.#rafId === null) {
      return;
    }

    this.#targetSpeed = target;
    this.#transitionFromSpeed = this.#currentSpeed;
    this.#transitionStart = performance.now();

    if (!this.#rafId) {
      this.#rafId = requestAnimationFrame(this.#tick);
    }
  }

  #tick = (now) => {
    const elapsed = now - this.#transitionStart;
    const progress = Math.min(elapsed / CompleteSystemOrbitSpeed.TRANSITION_MS, 1);
    const eased = 1 - (1 - progress) ** 3;

    this.#currentSpeed = this.#transitionFromSpeed + (this.#targetSpeed - this.#transitionFromSpeed) * eased;

    for (const animation of this.#animations) {
      animation.playbackRate = this.#currentSpeed;
    }

    if (progress < 1) {
      this.#rafId = requestAnimationFrame(this.#tick);
      return;
    }

    this.#currentSpeed = this.#targetSpeed;
    this.#rafId = null;
  };

  #pausePillFloat(pill) {
    for (const animation of pill.getAnimations()) {
      if (animation.animationName === CompleteSystemOrbitSpeed.PILL_FLOAT_ANIMATION) {
        animation.pause();
      }
    }
  }

  #resumePillFloat(pill) {
    for (const animation of pill.getAnimations()) {
      if (animation.animationName === CompleteSystemOrbitSpeed.PILL_FLOAT_ANIMATION) {
        animation.play();
      }
    }
  }

  #onMouseOver = (event) => {
    const pill = event.target.closest(CompleteSystemOrbitSpeed.PILL_SELECTOR);

    if (!pill) {
      return;
    }

    this.#pausePillFloat(pill);
    this.#setTargetSpeed(0);
  };

  #onMouseOut = (event) => {
    const pill = event.target.closest(CompleteSystemOrbitSpeed.PILL_SELECTOR);

    if (!pill) {
      return;
    }

    const relatedTarget = event.relatedTarget;

    if (relatedTarget instanceof Node && pill.contains(relatedTarget)) {
      return;
    }

    this.#resumePillFloat(pill);

    if (this.#heroCenter.querySelector(`${CompleteSystemOrbitSpeed.PILL_SELECTOR}:hover`)) {
      return;
    }

    this.#setTargetSpeed(this.#baseSpeed);
  };
}

if (!customElements.get('complete-system-orbit-speed')) {
  customElements.define('complete-system-orbit-speed', CompleteSystemOrbitSpeed);
}
